import React, { useState } from "react";

interface MedicationFormData {
  medicationName: string;
  medicationType: string;
  dosage: string;
  purpose: string;
  prescribedBy: string;
  startDate: string;
  endDate: string;
  frequencyType: "daily" | "weekly" | "as_needed" | "specific_days";
  timesPerDay: number;
  morningTime: string | "";
  afternoonTime: string | "";
  eveningTime: string | "";
  nightTime: string | "";
  mealRelation: "before_meal" | "with_meal" | "after_meal" | "between_meals" | "anytime";
  doseAmount: string;
  doseUnit: string;
  notes: string;
}

interface MedicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: MedicationFormData) => Promise<void>;
  isLoading?: boolean;
  selectedDate?: string;
}

const getDefaultFormData = (selectedDate?: string): MedicationFormData => ({
  medicationName: "",
  medicationType: "tablet",
  dosage: "",
  purpose: "",
  prescribedBy: "",
  startDate: selectedDate || new Date().toISOString().split("T")[0],
  endDate: "",
  frequencyType: "daily",
  timesPerDay: 1,
  morningTime: "",
  afternoonTime: "",
  eveningTime: "",
  nightTime: "",
  mealRelation: "after_meal",
  doseAmount: "1",
  doseUnit: "정",
  notes: "",
});

export const MedicationModal: React.FC<MedicationModalProps> = React.memo(
  ({ isOpen, onClose, onSubmit, isLoading = false, selectedDate }) => {
    const [formData, setFormData] = useState<MedicationFormData>(getDefaultFormData(selectedDate));
    const [error, setError] = useState<string>("");
    const [successMessage, setSuccessMessage] = useState<string>("");
    const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      setError("");
    };

    const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: parseInt(value) || 0,
      }));
    };

    const validateForm = (): boolean => {
      if (!formData.medicationName.trim()) {
        setError("약물명을 입력해주세요.");
        return false;
      }
      if (!formData.dosage.trim()) {
        setError("용량을 입력해주세요.");
        return false;
      }
      if (!formData.startDate) {
        setError("시작 날짜를 선택해주세요.");
        return false;
      }
      if (formData.endDate && formData.startDate > formData.endDate) {
        setError("종료 날짜가 시작 날짜보다 뒤여야 합니다.");
        return false;
      }
      if (formData.frequencyType === "daily" && formData.timesPerDay < 1) {
        setError("하루 복용 횟수는 1회 이상이어야 합니다.");
        return false;
      }

      // 복용 시간 검증: 선택된 시간 개수가 timesPerDay와 일치해야 함
      if (formData.frequencyType === "daily" || formData.frequencyType === "specific_days") {
        const selectedTimes = [
          formData.morningTime,
          formData.afternoonTime,
          formData.eveningTime,
          formData.nightTime,
        ].filter((time) => time !== "").length;

        if (selectedTimes !== formData.timesPerDay) {
          setError(
            `복용 시간을 정확히 ${formData.timesPerDay}개 선택해주세요. (현재: ${selectedTimes}개)`
          );
          return false;
        }
      }

      return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setError("");
      setSuccessMessage("");

      if (!validateForm()) return;

      try {
        await onSubmit(formData);
        setSuccessMessage("복약 정보가 등록되었습니다!");
        setTimeout(() => {
          setFormData(getDefaultFormData(selectedDate));
          setSuccessMessage("");
          onClose();
        }, 1500);
      } catch (err) {
        setError(err instanceof Error ? err.message : "등록 중 오류가 발생했습니다.");
      }
    };

    const handleClose = () => {
      // 상태 초기화
      setFormData(getDefaultFormData(selectedDate));
      setError(""); // 에러 명시적 초기화
      setSuccessMessage(""); // 성공 메시지 명시적 초기화
      onClose();
    };

    return (
      <div className="fixed inset-0 z-50 flex items-end bg-black bg-opacity-30 transition-all duration-300">
        {/* 모달 배경 - 항상 닫을 수 있음 */}
        <div
          className="absolute inset-0 z-[99]"
          onClick={(e) => {
            // 배경 자체만 클릭했을 때 모달 닫기
            if (e.target === e.currentTarget) {
              handleClose();
            }
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              handleClose();
            }
          }}
        />

        {/* 모달 컨텐츠 */}
        <div className="relative w-full bg-white rounded-t-3xl shadow-2xl animate-in slide-in-from-bottom duration-300 max-h-[90vh] overflow-y-auto z-[100]">
          {/* 헤더 */}
          <div className="sticky top-0 z-[101] bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
            <h2 className="text-[16px] font-bold text-slate-900">복약 정보 등록</h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors z-[102]"
              aria-label="닫기"
            >
              ✕
            </button>
          </div>

          {/* 폼 */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* 에러 메시지 - 토스트 형태로 고정 표시 */}
            {error && (
              <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[9500] max-w-[90vw] sm:max-w-sm bg-red-50 border-2 border-red-300 rounded-xl p-4 shadow-2xl animate-pulse pointer-events-auto">
                <p className="text-[13px] text-red-700 font-semibold text-center leading-relaxed">{error}</p>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setError("");
                  }}
                  className="mt-3 w-full px-3 py-2 bg-red-500 text-white text-[12px] font-semibold rounded-lg hover:bg-red-600 transition-colors active:bg-red-700"
                >
                  확인
                </button>
              </div>
            )}
            
            {/* 에러 표시 시 배경 오버레이 - 클릭 방지 */}
            {error && (
              <div 
                className="fixed inset-0 z-[9499] pointer-events-none"
                onClick={(e) => e.stopPropagation()}
              />
            )}

            {/* 성공 메시지 */}
            {successMessage && (
              <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[9999] max-w-[90vw] sm:max-w-sm bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4 shadow-2xl">
                <p className="text-[13px] text-emerald-700 font-semibold text-center leading-relaxed">{successMessage}</p>
              </div>
            )}

            {/* 약물명 */}
            <div>
              <label className="block text-[12px] font-semibold text-slate-900 mb-1">
                약물명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="medicationName"
                value={formData.medicationName}
                onChange={handleChange}
                placeholder="예: 타이레놀"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
            </div>

            {/* 약물 타입 & 용량 */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[12px] font-semibold text-slate-900 mb-1">
                  약물 형태
                </label>
                <select
                  name="medicationType"
                  value={formData.medicationType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  <option value="tablet">정제</option>
                  <option value="capsule">캡슐</option>
                  <option value="liquid">액상</option>
                  <option value="injection">주사</option>
                  <option value="topical">외용제</option>
                  <option value="inhaler">흡입제</option>
                  <option value="patch">패치</option>
                  <option value="drops">점안액</option>
                  <option value="powder">분말</option>
                  <option value="other">기타</option>
                </select>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-slate-900 mb-1">
                  용량 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="dosage"
                  value={formData.dosage}
                  onChange={handleChange}
                  placeholder="예: 500mg"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
            </div>

            {/* 용도 & 처방자 */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[12px] font-semibold text-slate-900 mb-1">용도</label>
                <input
                  type="text"
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleChange}
                  placeholder="예: 두통, 발열"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-slate-900 mb-1">처방자</label>
                <input
                  type="text"
                  name="prescribedBy"
                  value={formData.prescribedBy}
                  onChange={handleChange}
                  placeholder="예: 병원명/의사명"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
            </div>

            {/* 복약 기간 */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[12px] font-semibold text-slate-900 mb-1">
                  시작 날짜 <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-slate-900 mb-1">종료 날짜</label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
            </div>

            {/* 복약 빈도 */}
            <div>
              <label className="block text-[12px] font-semibold text-slate-900 mb-1">복약 빈도</label>
              <select
                name="frequencyType"
                value={formData.frequencyType}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="daily">매일</option>
                <option value="weekly">주 1회</option>
                <option value="specific_days">특정 요일</option>
                <option value="as_needed">필요시</option>
              </select>
            </div>

            {/* 1일 복용 횟수 */}
            {(formData.frequencyType === "daily" || formData.frequencyType === "specific_days") && (
              <div>
                <label className="block text-[12px] font-semibold text-slate-900 mb-1">
                  1일 복용 횟수
                </label>
                <input
                  type="number"
                  name="timesPerDay"
                  value={formData.timesPerDay}
                  onChange={handleNumberChange}
                  min="1"
                  max="4"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
            )}

            {/* 복용 시간 */}
            {(formData.frequencyType === "daily" || formData.frequencyType === "specific_days") && (
              <div>
                <label className="block text-[12px] font-semibold text-slate-900 mb-2">
                  복용 시간 <span className="text-red-500">*</span> (정확히 {formData.timesPerDay}개 선택)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] text-slate-600 block mb-1">아침</label>
                    <select
                      name="morningTime"
                      value={formData.morningTime}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="">미선택</option>
                      <option value="06:00">06:00</option>
                      <option value="07:00">07:00</option>
                      <option value="08:00">08:00</option>
                      <option value="09:00">09:00</option>
                      <option value="10:00">10:00</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-600 block mb-1">점심</label>
                    <select
                      name="afternoonTime"
                      value={formData.afternoonTime}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="">미선택</option>
                      <option value="11:00">11:00</option>
                      <option value="12:00">12:00</option>
                      <option value="13:00">13:00</option>
                      <option value="14:00">14:00</option>
                      <option value="15:00">15:00</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-600 block mb-1">저녁</label>
                    <select
                      name="eveningTime"
                      value={formData.eveningTime}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="">미선택</option>
                      <option value="17:00">17:00</option>
                      <option value="18:00">18:00</option>
                      <option value="19:00">19:00</option>
                      <option value="20:00">20:00</option>
                      <option value="21:00">21:00</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-600 block mb-1">취침 전</label>
                    <select
                      name="nightTime"
                      value={formData.nightTime}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="">미선택</option>
                      <option value="21:00">21:00</option>
                      <option value="22:00">22:00</option>
                      <option value="23:00">23:00</option>
                      <option value="00:00">00:00</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* 식사 관계 & 복용량 */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[12px] font-semibold text-slate-900 mb-1">식사 관계</label>
                <select
                  name="mealRelation"
                  value={formData.mealRelation}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  <option value="before_meal">식전</option>
                  <option value="with_meal">식중</option>
                  <option value="after_meal">식후</option>
                  <option value="between_meals">공복</option>
                  <option value="anytime">상관없음</option>
                </select>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-slate-900 mb-1">1회 복용량</label>
                <div className="flex gap-2 items-stretch">
                  <input
                    type="number"
                    name="doseAmount"
                    value={formData.doseAmount}
                    onChange={handleChange}
                    step="0.5"
                    min="0"
                    placeholder="수량"
                    className="w-16 px-2 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500 text-center"
                  />
                  <select
                    name="doseUnit"
                    value={formData.doseUnit}
                    onChange={handleChange}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500 min-w-0"
                  >
                    <option value="정">정</option>
                    <option value="캡슐">캡슐</option>
                    <option value="ml">ml</option>
                    <option value="포">포</option>
                    <option value="회">회</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 메모 */}
            <div>
              <label className="block text-[12px] font-semibold text-slate-900 mb-1">메모</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="복용 시 주의사항, 부작용 등을 기록하세요"
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
              />
            </div>

            {/* 버튼 */}
            <div className="flex gap-2 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 font-semibold text-[13px] hover:bg-slate-50 transition-colors disabled:opacity-50"
                disabled={isLoading}
              >
                취소
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 rounded-lg bg-violet-600 text-white font-semibold text-[13px] hover:bg-violet-700 transition-colors disabled:opacity-50"
                disabled={isLoading}
              >
                {isLoading ? "등록 중..." : "복약 정보 등록"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
);

MedicationModal.displayName = "MedicationModal";
